/**
 * Two tabs of the staff email page: how much a domain may send as it warms
 * up, with the lines at which Connect's watch throttles and stops it; and the
 * month's spend at the mail service with the account's standing there.
 *
 * The API owns every rule: whether a number is in range, whether a rate may
 * sit above its pause line. Its refusal is shown under the field it is about.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  ReadOnly,
  StatCard,
  api,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

import { refusal } from './api-refusal';
import { EMAIL_ENDPOINT, emailKeys, type EmailClientApi, type EmailLimits, type EmailOverview } from './email';

type Path = string[];

function getAt(value: unknown, path: Path): string {
  let cursor: unknown = value;
  for (const key of path) cursor = (cursor as Record<string, unknown>)[key];
  return String(cursor ?? '');
}

function setAt<T>(value: T, path: Path, next: number): T {
  const copy = structuredClone(value) as Record<string, unknown>;
  let cursor = copy;
  for (const key of path.slice(0, -1)) cursor = cursor[key] as Record<string, unknown>;
  cursor[path[path.length - 1] as string] = next;
  return copy as T;
}

const LADDER: { label: string; path: Path; hint: string }[] = [
  { label: 'New — a day', path: ['new', 'daily'], hint: 'A domain that has just been verified.' },
  { label: 'New — an hour', path: ['new', 'hourly'], hint: '' },
  { label: 'Building — a day', path: ['building', 'daily'], hint: 'Promoted from new.' },
  { label: 'Building — an hour', path: ['building', 'hourly'], hint: '' },
  { label: 'Established — a day', path: ['established', 'daily'], hint: 'Beyond this, give a domain custom caps.' },
  { label: 'Established — an hour', path: ['established', 'hourly'], hint: '' },
];
const PROMOTION: { label: string; path: Path; hint: string }[] = [
  { label: 'New → building: days', path: ['to_building', 'days'], hint: 'Days on the stage.' },
  { label: 'New → building: emails', path: ['to_building', 'sent'], hint: 'Accepted since the stage began.' },
  { label: 'Building → established: days', path: ['to_established', 'days'], hint: '' },
  { label: 'Building → established: emails', path: ['to_established', 'sent'], hint: '' },
];
const LINES: { label: string; path: Path; hint: string }[] = [
  { label: 'Throttle at bounces (%)', path: ['throttle_bounce'], hint: 'Half caps from here.' },
  { label: 'Stop at bounces (%)', path: ['pause_bounce'], hint: 'The domain stops; two stopped stop the client.' },
  { label: 'Throttle at complaints (%)', path: ['throttle_complaint'], hint: '' },
  { label: 'Stop at complaints (%)', path: ['pause_complaint'], hint: '' },
  { label: 'Fewest emails for a rate', path: ['minimum_volume'], hint: 'Below this in a window, no rate is acted on.' },
  { label: 'Spike: times a usual day', path: ['spike_factor'], hint: 'A day this many times the usual halves the caps for a day.' },
];

export function EmailLimitsCard({ limits }: { limits: EmailLimits }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const [draft, setDraft] = useState(limits);
  const changed = JSON.stringify(draft) !== JSON.stringify(limits);
  const save = useMutation({
    mutationFn: () => api.put<EmailOverview>(`${EMAIL_ENDPOINT}/limits`, draft),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success('Limits saved', 'Every send from now on is held to them.');
    },
  });
  const refused = refusal(save.error);

  const field = (item: { label: string; path: Path; hint: string }) => (
    <Input
      key={item.path.join('.')}
      label={item.label}
      inputMode="decimal"
      value={getAt(draft, item.path)}
      hint={item.hint || undefined}
      error={refused?.fields[item.path.join('.')] ?? refused?.fields[item.path[0] as string]}
      onChange={(event) => {
        save.reset();
        const typed = Number(event.target.value);
        if (!Number.isNaN(typed)) setDraft((was) => setAt(was, item.path, typed));
      }}
    />
  );

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Warm-up"
            description="How much one domain may send while it builds a record with the mailbox providers. Counted over the last 24 hours and the last 60 minutes, from what the mail service accepted. A send that does not fit is refused whole, and costs nothing."
          />
          <CardBody className="space-y-4">
            {canEdit ? null : <Alert tone="info">You can see these, not change them.</Alert>}
            <div className="grid gap-3 sm:grid-cols-2">{LADDER.map(field)}</div>
            <div className="grid gap-3 sm:grid-cols-2">{PROMOTION.map(field)}</div>
            <p className="text-xs text-muted">
              A domain is promoted only while its bounce and complaint rates are under the throttle lines below.
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="When Connect steps in"
            description="Rates over the last 24 hours and 7 days, whichever is worse. Connect acts at about half the mail service's own review lines (5% bounces, 0.1% complaints), so it acts first. A stop is lifted only by a person."
          />
          <CardBody className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">{LINES.map(field)}</div>
            {refused?.general.length ? <Alert tone="danger">{refused.general.join(' ')}</Alert> : null}
            {canEdit && changed ? (
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    save.reset();
                    setDraft(limits);
                  }}
                >
                  Discard
                </Button>
                <Button loading={save.isPending} onClick={() => save.mutate()}>
                  Save limits
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </ReadOnly>
  );
}

const CLIENT_API: { label: string; path: Path; hint: string }[] = [
  { label: 'Own content — an hour', path: ['direct_per_hour'], hint: 'Addresses, per client, beside its domains’ caps.' },
  { label: 'Own content — a day', path: ['direct_per_day'], hint: '' },
  { label: 'Stop own content at bounces (%)', path: ['direct_pause_bounce'], hint: 'Over the last 24 hours. Templates still send.' },
  { label: 'Stop own content at complaints (%)', path: ['direct_pause_complaint'], hint: '' },
  { label: 'Fewest emails for a rate', path: ['direct_minimum_volume'], hint: 'Below this in 24 hours, no rate is acted on.' },
  { label: 'Addresses in one email with copies', path: ['max_copy_addresses'], hint: 'To, Cc and Bcc together. At most 50.' },
];

/**
 * The lines of the client API features a client is given on its API access card: how much of
 * its own email content it may send, where that stops by itself, and how many addresses one
 * email with Cc and Bcc may name.
 */
export function EmailClientApiCard({ policy }: { policy: EmailClientApi }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const [draft, setDraft] = useState(policy);
  const changed = JSON.stringify(draft) !== JSON.stringify(policy);
  const save = useMutation({
    mutationFn: () => api.put<EmailOverview>(`${EMAIL_ENDPOINT}/client-api`, draft),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success('Saved', 'Every send from now on is held to them.');
    },
  });
  const refused = refusal(save.error);

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Client API: own content and copies"
          description="For clients given “Own email content” or “Cc and Bcc” on their API access card. A client’s own content has its own limits and stops by itself when its bounces or complaints pass these lines — only a person restarts it, from the client’s card."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {CLIENT_API.map((item) => (
              <Input
                key={item.path.join('.')}
                label={item.label}
                inputMode="decimal"
                value={getAt(draft, item.path)}
                hint={item.hint || undefined}
                error={refused?.fields[item.path.join('.')]}
                onChange={(event) => {
                  save.reset();
                  const typed = Number(event.target.value);
                  if (!Number.isNaN(typed)) setDraft((was) => setAt(was, item.path, typed));
                }}
              />
            ))}
          </div>
          {refused?.general.length ? <Alert tone="danger">{refused.general.join(' ')}</Alert> : null}
          {canEdit && changed ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  save.reset();
                  setDraft(policy);
                }}
              >
                Discard
              </Button>
              <Button loading={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}

const ENFORCEMENT: Record<string, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  HEALTHY: { label: 'Healthy', tone: 'success' },
  PROBATION: { label: 'On probation', tone: 'warning' },
  SHUTDOWN: { label: 'Shut down', tone: 'danger' },
};

export function EmailBudgetCards({ overview }: { overview: EmailOverview }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.email.edit');
  const { budget, account } = overview;
  const [draft, setDraft] = useState({
    aws_rate_per_1000: budget.aws_rate_per_1000,
    monthly_budget_usd: budget.monthly_budget_usd,
  });
  const changed =
    draft.aws_rate_per_1000 !== budget.aws_rate_per_1000 || draft.monthly_budget_usd !== budget.monthly_budget_usd;
  const save = useMutation({
    mutationFn: () => api.put<EmailOverview>(`${EMAIL_ENDPOINT}/budget`, draft),
    onSuccess: (data) => {
      queryClient.setQueryData(emailKeys.overview, data);
      toast.success('Budget saved');
    },
  });
  const check = useMutation({
    mutationFn: () => api.post<{ stopped: number }>(`${EMAIL_ENDPOINT}/check`, {}),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['email'] });
      toast.success(
        'Checked',
        data.stopped ? `${formatNumber(data.stopped)} domain(s) were stopped.` : 'Nothing needed stopping.',
      );
    },
  });
  const refused = refusal(save.error);
  const used = Math.round(budget.used * 100);
  const state = account && !('error' in account) ? account : null;
  const standing = state ? (ENFORCEMENT[state.enforcement] ?? { label: state.enforcement, tone: 'warning' as const }) : null;

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={`Spend at the mail service — ${budget.month}`}
            description="Worked out by Connect from what it sent, with no billing call (that would itself cost money). At 80% staff are told; at 100% clients' email stops and Connect's own sign-in mail carries on. Raising the budget lifts it at once."
            actions={<Badge tone={used >= 100 ? 'danger' : used >= 80 ? 'warning' : 'success'}>{used}% used</Badge>}
          />
          <CardBody className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-4">
              <StatCard label="Cost so far" value={`$${budget.cost_usd}`} hint={`of $${budget.budget_usd}`} />
              <StatCard label="Clients' emails" value={formatNumber(budget.client_messages)} />
              <StatCard label="Connect's own" value={formatNumber(budget.own_messages)} />
              <StatCard label="Projects" value={formatNumber(budget.projects)} hint="The first 1,000 are free." />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Mail service price per 1,000 (US$)"
                inputMode="decimal"
                value={draft.aws_rate_per_1000}
                error={refused?.fields.aws_rate_per_1000}
                onChange={(event) => {
                  save.reset();
                  setDraft((was) => ({ ...was, aws_rate_per_1000: event.target.value.trim() }));
                }}
              />
              <Input
                label="Monthly budget (US$)"
                inputMode="decimal"
                value={draft.monthly_budget_usd}
                error={refused?.fields.monthly_budget_usd}
                onChange={(event) => {
                  save.reset();
                  setDraft((was) => ({ ...was, monthly_budget_usd: event.target.value.trim() }));
                }}
              />
            </div>
            {refused?.general.length ? <Alert tone="danger">{refused.general.join(' ')}</Alert> : null}
            {canEdit && changed ? (
              <div className="flex justify-end">
                <Button loading={save.isPending} onClick={() => save.mutate()}>
                  Save budget
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
  
        <Card>
          <CardHeader
            title="The account at the mail service"
            description="Read from the service each time this page opens."
            actions={standing ? <Badge tone={standing.tone}>{standing.label}</Badge> : null}
          />
          <CardBody className="space-y-3">
            {account === null ? (
              <p className="text-sm text-muted">No mail service is configured here, so there is nothing to read.</p>
            ) : 'error' in account ? (
              <Alert tone="warning">{account.error}</Alert>
            ) : (
              <>
                {account.production ? null : (
                  <Alert tone="warning">
                    The account is in the sandbox: it sends only to verified addresses, 200 a day.
                  </Alert>
                )}
                {account.sending_enabled ? null : (
                  <Alert tone="danger">The mail service has stopped this account sending.</Alert>
                )}
                {account.unrecorded ? (
                  <Alert tone="danger">
                    The service counted {formatNumber(account.sent_24_hours)} emails in 24 hours and Connect recorded{' '}
                    {formatNumber(account.recorded_24_hours)}: something sent mail Connect does not know about.
                  </Alert>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-4">
                  <StatCard label="Sent in 24 hours" value={formatNumber(account.sent_24_hours)} />
                  <StatCard label="Recorded by Connect" value={formatNumber(account.recorded_24_hours)} />
                  <StatCard label="Allowed in 24 hours" value={formatNumber(account.max_24_hours)} />
                  <StatCard label="Per second" value={formatNumber(account.max_per_second)} />
                </div>
              </>
            )}
            {canEdit ? (
              <div className="flex justify-end">
                <Button variant="secondary" loading={check.isPending} onClick={() => check.mutate()}>
                  Check every project now
                </Button>
              </div>
            ) : null}
            <p className="text-xs text-muted">
              The service's own verdict on each project is read every 15 minutes: one it disabled stops that client, a high
              impact stops the project's domains, a low one throttles them.
            </p>
          </CardBody>
        </Card>
      </div>
    </ReadOnly>
  );
}
